import { AlpineComponent } from '@alpinejs/csp';
import { toCircle, interpolateAll } from 'flubber';

import config from './config';
import { createSVG, createPath } from './helpers/svg';
import { getMousePosition } from './helpers/mouse';
import { getNumberFromRange } from './helpers/calc';
import { easeOutElastic } from '../../../javascripts/helpers/easings';

const steps = 4;
const offsetX = 6;
const offsetY = 2;

const seconds = 3;
const fps = 30;
const maxFrames = seconds * fps;
const maxDuration = maxFrames * config.shapes.length;

const AnimatedLogo = (): AlpineComponent => ({
  container: undefined,
  svg: undefined,
  group: undefined,
  logo: undefined,
  paths: [],
  steps,

  offsetY,
  offsetX,

  width: steps * offsetX + 70,
  height: steps * offsetY + 70,

  options: { maxSegmentLength: 2 },

  index: 0,
  mouse: { x: 1, y: 0 },

  shapes: [],

  createInterpolatedShapes() {
    const baseFrom: string[] = [];
    const baseTo: string[] = [];

    config.shapes.forEach((item, i) => {
      baseFrom.push(item);
      baseTo.push(config.shapes[i + 1 === config.shapes.length ? 0 : i + 1]);
    });

    const baseInterpolators = interpolateAll(baseFrom, baseTo, this.options);

    const stepsFrom: string[] = [];

    baseInterpolators.forEach((interpolator: any) => {
      for (let frame = 1; frame <= maxFrames; frame += 1) {
        stepsFrom.push(interpolator(frame / maxFrames));
      }
    });

    const stepInterpolators = Array.from({ length: stepsFrom.length }, (_, i) =>
      toCircle(stepsFrom[i], 0, 0, 20, this.options)
    );

    const list: string[][] = [];

    for (let i = 0; i <= steps; i += 1) {
      list.push([]);
      const step = i / steps;

      stepInterpolators.forEach((interpolator: any) => {
        list[i].push(interpolator(step));
      });
    }

    return list;
  },

  updatePaths() {
    const items: SVGPathElement[] = Array.from(this.paths);
    items.forEach((path: SVGPathElement, i: number) => {
      path.setAttribute('d', this.shapes[i][this.index]);
      this.centerPath(path, i);
    });
  },

  handleMouseMove(event: MouseEvent) {
    const { x, y } = getMousePosition(event);
    const deltaX = x - this.mouse.x;
    const deltaY = y - this.mouse.y;

    this.mouse.x += deltaX * easeOutElastic(0.02);
    this.mouse.y += deltaY * easeOutElastic(0.02);

    this.updateGroup();
    this.updatePaths();
    this.updateLogo();
  },

  handleScroll() {
    const docHeight = document.body.scrollHeight;

    const scrollPercent = window.scrollY / (docHeight - window.innerHeight);
    const frame = Math.floor((maxDuration - 1) * scrollPercent);

    if (frame > maxDuration - 1) {
      this.index = maxDuration - 1;
    } else if (frame < 0) {
      this.index = 0;
    } else {
      this.index = frame;
    }

    this.updatePaths();
  },

  centerPath(path: SVGPathElement, i: number) {
    const rev = (this.paths.length - i) * 1 - 1;
    const box = path.getBBox();

    const tx =
      (this.width - box.width) / 2 - rev * offsetX * this.mouse.x - box.x;
    const ty =
      (this.height - box.height) / 2 - rev * offsetY * this.mouse.y - box.y;

    path.setAttribute('transform', `translate(${tx}, ${ty})`);
  },

  updateLogo() {
    const box = this.logo.getBBox();

    const tx = (this.width - box.width) / 2 - box.x;
    const ty = (this.height - box.height) / 2 - box.y;

    this.logo.setAttribute('transform', `translate(${tx}, ${ty})`);
  },

  updateGroup() {
    this.group.setAttribute(
      'transform',
      `translate(${offsetX * (steps / 2) * this.mouse.x},${
        offsetY * (steps / 2) * this.mouse.y
      })`
    );
  },

  createLogo() {
    this.logo = createPath({ d: config.m, fill: 'currentColor' });
    this.group.appendChild(this.logo);
    this.updateLogo();
  },

  createPaths() {
    this.paths = [];

    for (let i = 0; i <= steps; i += 1) {
      const step = i / steps;

      const stroke = config.colors[i];
      const strokeWidth = getNumberFromRange(1.3, 2.1, step);

      const path = createPath({
        d: this.shapes[i][0],
        stroke,
        strokeWidth
      });

      this.group.appendChild(path);
      this.paths = [...this.paths, path];
    }

    this.updatePaths();
  },

  async init() {
    this.svg = createSVG({ width: this.width, height: this.height });
    this.$root.appendChild(this.svg);

    this.group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.svg.appendChild(this.group);
    this.updateGroup();

    this.shapes = await this.createInterpolatedShapes();

    this.createPaths();
    this.createLogo();
  }
});

export default AnimatedLogo;
